import { useState, useMemo } from 'react';
import { useProfile, useCashflows } from '../../hooks/useApi';
import { formatAmount } from '../../utils/numberFormat';
import { Country, Currency } from '../../types/api.types';
import styles from './HealthcareBridgePage.module.scss';

const HEALTHCARE_COSTS: Record<
    Country,
    {
        annual: number;
        currency: Currency;
        inflation: number;
        label: string;
        eligibilityAge: number;
        description: string;
    }
> = {
    US: {
        annual: 12000,
        currency: 'USD',
        inflation: 5.0,
        label: 'US Private Health Insurance',
        eligibilityAge: 65,
        description: 'Medicare eligibility begins at 65. Before that, you need ACA marketplace or COBRA coverage.',
    },
    CAN: {
        annual: 3000,
        currency: 'CAD',
        inflation: 4.0,
        label: 'Canada Supplemental Health',
        eligibilityAge: 65,
        description:
            'Provincial health covers basics, but early retirees often need supplemental coverage for dental, vision, and prescriptions.',
    },
    INDIA: {
        annual: 150000,
        currency: 'INR',
        inflation: 8.0,
        label: 'India Private Health Insurance',
        eligibilityAge: 95,
        description: 'No universal government healthcare — private health insurance is needed throughout retirement.',
    },
};

function computeBridgeCost(
    annualCost: number,
    inflationRate: number,
    gapYears: number,
): { totalCost: number; yearlyBreakdown: number[] } {
    const yearlyBreakdown: number[] = [];
    let totalCost = 0;
    for (let y = 0; y < gapYears; y++) {
        const yearlyCost = annualCost * Math.pow(1 + inflationRate / 100, y);
        yearlyBreakdown.push(Math.round(yearlyCost));
        totalCost += yearlyCost;
    }
    return { totalCost: Math.round(totalCost), yearlyBreakdown };
}

export function HealthcareBridgePage() {
    const { profile, loading: profileLoading } = useProfile();
    const { cashflows, addCashflow } = useCashflows();

    const [savedMessage, setSavedMessage] = useState<string | null>(null);
    const [savingCountry, setSavingCountry] = useState<Country | null>(null);

    const [usAnnual, setUsAnnual] = useState(HEALTHCARE_COSTS.US.annual);
    const [usEligibility, setUsEligibility] = useState(HEALTHCARE_COSTS.US.eligibilityAge);
    const [canAnnual, setCanAnnual] = useState(HEALTHCARE_COSTS.CAN.annual);
    const [canEligibility, setCanEligibility] = useState(HEALTHCARE_COSTS.CAN.eligibilityAge);
    const [indiaAnnual, setIndiaAnnual] = useState(HEALTHCARE_COSTS.INDIA.annual);
    const [indiaEndAge, setIndiaEndAge] = useState(75);

    const residencyCountries = useMemo(
        () => new Set((profile?.residencyHistory ?? []).map((r) => r.country)),
        [profile],
    );

    const retirementAge = profile?.targetRetirementAge ?? 55;
    const existingBridge = cashflows.filter((c) => c.category === 'HEALTHCARE_BRIDGE');

    const fmt = (amt: number, cur: string) => formatAmount(Math.round(amt), cur as Currency);

    const showSuccess = (label: string) => {
        setSavedMessage(`${label} healthcare bridge added to your projection.`);
        setTimeout(() => setSavedMessage(null), 4000);
    };

    const saveHealthcareBridge = async (
        country: Country,
        annualCost: number,
        currency: Currency,
        inflation: number,
        startAge: number,
        endAge: number,
    ) => {
        setSavingCountry(country);
        try {
            await addCashflow({
                type: 'EXPENSE',
                category: 'HEALTHCARE_BRIDGE',
                country,
                amount: annualCost,
                currency,
                growthRate: inflation,
                startAge,
                endAge,
            });
            showSuccess(HEALTHCARE_COSTS[country].label);
        } finally {
            setSavingCountry(null);
        }
    };

    if (profileLoading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading...</p>
            </div>
        );
    }

    const usGapYears = Math.max(0, usEligibility - retirementAge);
    const canGapYears = Math.max(0, canEligibility - retirementAge);
    const indiaGapYears = Math.max(0, indiaEndAge - retirementAge);

    const usBridge = computeBridgeCost(usAnnual, HEALTHCARE_COSTS.US.inflation, usGapYears);
    const canBridge = computeBridgeCost(canAnnual, HEALTHCARE_COSTS.CAN.inflation, canGapYears);
    const indiaBridge = computeBridgeCost(indiaAnnual, HEALTHCARE_COSTS.INDIA.inflation, indiaGapYears);

    const hasAny = residencyCountries.has('US') || residencyCountries.has('CAN') || residencyCountries.has('INDIA');

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Healthcare Bridge Estimator</h1>
                <p className={styles.subtitle}>
                    Estimate private healthcare costs between early retirement (age {retirementAge}) and government
                    healthcare eligibility. These costs flow into your Monte Carlo projection as expenses.
                </p>
            </div>

            {savedMessage && <div className={styles.successBanner}>{savedMessage}</div>}

            {existingBridge.length > 0 && (
                <div className={styles.existingSection}>
                    <h2 className={styles.sectionTitle}>Already in Projection</h2>
                    <div className={styles.existingList}>
                        {existingBridge.map((cf) => (
                            <div key={cf.cashFlowId} className={styles.existingItem}>
                                <span className={styles.existingLabel}>
                                    {cf.country === 'US' ? '🇺🇸' : cf.country === 'CAN' ? '🇨🇦' : '🇮🇳'} Healthcare Bridge
                                </span>
                                <span className={styles.existingAmount}>
                                    {fmt(cf.amount, cf.currency)}/yr · age {cf.startAge}–{cf.endAge}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className={styles.cardsGrid}>
                {residencyCountries.has('US') && (
                    <div className={styles.bridgeCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <span className={styles.cardFlag}>🇺🇸</span>
                                <h2 className={styles.cardTitle}>{HEALTHCARE_COSTS.US.label}</h2>
                            </div>
                            <span className={styles.cardBadge}>US</span>
                        </div>
                        <p className={styles.cardDesc}>{HEALTHCARE_COSTS.US.description}</p>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Annual premium (USD)
                                <span className={styles.inputValue}>${usAnnual.toLocaleString()}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={3000}
                                max={30000}
                                step={500}
                                value={usAnnual}
                                onChange={(e) => setUsAnnual(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>$3k</span>
                                <span>$30k</span>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Medicare eligibility age
                                <span className={styles.inputValue}>{usEligibility}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={60}
                                max={70}
                                value={usEligibility}
                                onChange={(e) => setUsEligibility(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>60</span>
                                <span>70</span>
                            </div>
                        </div>

                        <div className={styles.estimate}>
                            <span className={styles.estimateLabel}>Bridge period</span>
                            <span className={styles.estimatePeriod}>
                                Age {retirementAge} → {usEligibility} ({usGapYears} years)
                            </span>
                            <span className={styles.estimateLabel}>Total estimated cost</span>
                            <span className={styles.estimateAmount}>${usBridge.totalCost.toLocaleString()}</span>
                            <span className={styles.estimateSubtext}>
                                Starting at ${usAnnual.toLocaleString()}/yr with {HEALTHCARE_COSTS.US.inflation}%
                                medical inflation
                            </span>
                        </div>

                        <button
                            className={styles.addButton}
                            onClick={() =>
                                saveHealthcareBridge(
                                    'US',
                                    usAnnual,
                                    'USD',
                                    HEALTHCARE_COSTS.US.inflation,
                                    retirementAge,
                                    usEligibility - 1,
                                )
                            }
                            disabled={savingCountry === 'US' || usGapYears <= 0}
                        >
                            {savingCountry === 'US'
                                ? 'Saving…'
                                : usGapYears <= 0
                                  ? 'No gap (already eligible)'
                                  : 'Add to Projection'}
                        </button>
                    </div>
                )}

                {residencyCountries.has('CAN') && (
                    <div className={styles.bridgeCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <span className={styles.cardFlag}>🇨🇦</span>
                                <h2 className={styles.cardTitle}>{HEALTHCARE_COSTS.CAN.label}</h2>
                            </div>
                            <span className={styles.cardBadge}>CAN</span>
                        </div>
                        <p className={styles.cardDesc}>{HEALTHCARE_COSTS.CAN.description}</p>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Annual supplemental cost (CAD)
                                <span className={styles.inputValue}>C${canAnnual.toLocaleString()}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={1000}
                                max={15000}
                                step={500}
                                value={canAnnual}
                                onChange={(e) => setCanAnnual(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>C$1k</span>
                                <span>C$15k</span>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Coverage end age
                                <span className={styles.inputValue}>{canEligibility}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={60}
                                max={75}
                                value={canEligibility}
                                onChange={(e) => setCanEligibility(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>60</span>
                                <span>75</span>
                            </div>
                        </div>

                        <div className={styles.estimate}>
                            <span className={styles.estimateLabel}>Bridge period</span>
                            <span className={styles.estimatePeriod}>
                                Age {retirementAge} → {canEligibility} ({canGapYears} years)
                            </span>
                            <span className={styles.estimateLabel}>Total estimated cost</span>
                            <span className={styles.estimateAmount}>C${canBridge.totalCost.toLocaleString()}</span>
                            <span className={styles.estimateSubtext}>
                                Starting at C${canAnnual.toLocaleString()}/yr with {HEALTHCARE_COSTS.CAN.inflation}%
                                medical inflation
                            </span>
                        </div>

                        <button
                            className={styles.addButton}
                            onClick={() =>
                                saveHealthcareBridge(
                                    'CAN',
                                    canAnnual,
                                    'CAD',
                                    HEALTHCARE_COSTS.CAN.inflation,
                                    retirementAge,
                                    canEligibility - 1,
                                )
                            }
                            disabled={savingCountry === 'CAN' || canGapYears <= 0}
                        >
                            {savingCountry === 'CAN'
                                ? 'Saving…'
                                : canGapYears <= 0
                                  ? 'No gap (already eligible)'
                                  : 'Add to Projection'}
                        </button>
                    </div>
                )}

                {residencyCountries.has('INDIA') && (
                    <div className={styles.bridgeCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <span className={styles.cardFlag}>🇮🇳</span>
                                <h2 className={styles.cardTitle}>{HEALTHCARE_COSTS.INDIA.label}</h2>
                            </div>
                            <span className={styles.cardBadge}>India</span>
                        </div>
                        <p className={styles.cardDesc}>{HEALTHCARE_COSTS.INDIA.description}</p>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Annual premium (INR)
                                <span className={styles.inputValue}>{fmt(indiaAnnual, 'INR')}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={50000}
                                max={500000}
                                step={10000}
                                value={indiaAnnual}
                                onChange={(e) => setIndiaAnnual(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>50k</span>
                                <span>5L</span>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Coverage end age
                                <span className={styles.inputValue}>{indiaEndAge}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={65}
                                max={95}
                                value={indiaEndAge}
                                onChange={(e) => setIndiaEndAge(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>65</span>
                                <span>95</span>
                            </div>
                        </div>

                        <div className={styles.estimate}>
                            <span className={styles.estimateLabel}>Coverage period</span>
                            <span className={styles.estimatePeriod}>
                                Age {retirementAge} → {indiaEndAge} ({indiaGapYears} years)
                            </span>
                            <span className={styles.estimateLabel}>Total estimated cost</span>
                            <span className={styles.estimateAmount}>{fmt(indiaBridge.totalCost, 'INR')}</span>
                            <span className={styles.estimateSubtext}>
                                Starting at {fmt(indiaAnnual, 'INR')}/yr with {HEALTHCARE_COSTS.INDIA.inflation}%
                                medical inflation
                            </span>
                        </div>

                        <button
                            className={styles.addButton}
                            onClick={() =>
                                saveHealthcareBridge(
                                    'INDIA',
                                    indiaAnnual,
                                    'INR',
                                    HEALTHCARE_COSTS.INDIA.inflation,
                                    retirementAge,
                                    indiaEndAge,
                                )
                            }
                            disabled={savingCountry === 'INDIA' || indiaGapYears <= 0}
                        >
                            {savingCountry === 'INDIA'
                                ? 'Saving…'
                                : indiaGapYears <= 0
                                  ? 'Already past retirement'
                                  : 'Add to Projection'}
                        </button>
                    </div>
                )}

                {!hasAny && (
                    <div className={styles.emptyState}>
                        <p>No residency history found.</p>
                        <p className={styles.emptyHint}>
                            Add your residency timeline on the <strong>Residency</strong> page to see country-specific
                            healthcare estimates.
                        </p>
                    </div>
                )}
            </div>

            <div className={styles.disclaimer}>
                <strong>Estimates only.</strong> Healthcare costs vary widely by plan type, family size, location, and
                health status. US estimates are based on ACA silver-tier marketplace premiums for individuals. India
                estimates assume a family floater plan. Adjust sliders to match your situation.
            </div>
        </div>
    );
}
